import { OPERATIONS, rnd, shuffle } from './operations';
import { formatWholeNumber } from '../util/numberFormat';
import { genAddition } from '../planets/additionMissions';
import { genSubtraction } from '../planets/subtractionMissions';
import { genMultiplication } from '../planets/multiplicationMissions';
import { genDivision } from '../planets/divisionMissions';

export class MathEngine {
  allowedOps() { return ['suma']; }

  buildProblem(opId, a, b) {
    const op = OPERATIONS[opId], answer = op.apply(a,b);
    const pool=op.distractors(a,b,answer).filter(v=>Number.isInteger(v)&&v>=0&&v!==answer);
    const uniq=[...new Set(pool)].sort((x,y)=>Math.abs(x-answer)-Math.abs(y-answer));
    const distractors=shuffle(uniq.slice(0,5)).slice(0,2);
    while(distractors.length<2){const c=answer+rnd(1,5)*(Math.random()<.5?1:-1);if(c>=0&&c!==answer&&!distractors.includes(c))distractors.push(c);}
    const choices=shuffle([{value:answer,correct:true},{value:distractors[0],correct:false},{value:distractors[1],correct:false}]);
    return {a,b,op:opId,symbol:op.symbol,text:`${formatWholeNumber(a)} ${op.symbol} ${formatWholeNumber(b)}`,answer,choices,correctIndex:choices.findIndex(c=>c.correct)};
  }

  buildDivisionProblem({a,b,quotient,remainder}) {
    // En una misión exacta NINGUNA alternativa menciona residuo.
    // Los cocientes grandes siempre conservan las comas. En las respuestas
    // con residuo usan la notación compacta de juego: cociente Rresiduo.
    const format = formatWholeNumber;
    const label = (q, r) => r
      ? `${format(q)} R${format(r)}`
      : format(q);
    const raw = remainder === 0
      ? [
          {value:quotient,remainder:0,correct:true},
          {value:quotient+1,remainder:0,correct:false},
          {value:Math.max(0,quotient-1),remainder:0,correct:false},
          {value:quotient+2,remainder:0,correct:false},
        ]
      : [
          {value:quotient,remainder,correct:true},
          {value:quotient+1,remainder,correct:false},
          {value:Math.max(0,quotient-1),remainder,correct:false},
          {value:quotient+2,remainder:Math.max(1,remainder-1),correct:false},
          {value:Math.max(0,quotient-2),remainder:Math.min(b-1,remainder+1),correct:false},
        ];
    const seen=new Set();
    const unique=raw.filter(x=>{const k=`${x.value}_${x.remainder}`;if(seen.has(k))return false;seen.add(k);return true;});
    const correct=unique.find(x=>x.correct);
    const choices=shuffle([correct,...shuffle(unique.filter(x=>!x.correct)).slice(0,2)]).map(x=>({...x,label:label(x.value,x.remainder)}));
    return {a,b,op:'division',symbol:'÷',text:`${formatWholeNumber(a)} ÷ ${formatWholeNumber(b)}`,answer:quotient,remainder,choices,correctIndex:choices.findIndex(c=>c.correct)};
  }

  generate(level=1){const ops=this.allowedOps(level),opId=ops[rnd(0,ops.length-1)],op=OPERATIONS[opId],g=op.gen(level);return {...this.buildProblem(opId,opId==='resta'?Math.max(g.a,g.b):g.a,opId==='resta'?Math.min(g.a,g.b):g.b),level};}
  generateLevel(count){return Array.from({length:count},(_,i)=>this.generate(i+1));}

  digitCount(value) {
    return Math.max(1, String(Math.abs(value)).length);
  }

  hasZeroOperand(g) {
    return g.a === 0 || g.b === 0;
  }

  isTrivial(g, operation) {
    if (g.a === 0 && g.b === 0) return true;
    if (operation === 'mult' && g.a === 1 && g.b === 1) return true;
    return false;
  }

  canonicalMissionKey(g, operation) {
    // Suma y multiplicación son conmutativas: 3 + 8 y 8 + 3 no deben
    // aparecer como ejercicios diferentes durante la misma partida.
    if (operation === 'suma' || operation === 'mult') {
      return [g.a, g.b].sort((a, b) => a - b).join('_');
    }
    return String(g.a) + '_' + String(g.b) + '_' + String(g.remainder || 0);
  }

  skillTagFor(g, operation) {
    const aDigits = this.digitCount(g.a);
    const bDigits = this.digitCount(g.b);
    if (operation === 'division') {
      return 'division.' + aDigits + 'd_' + bDigits + 'd.' + (g.remainder ? 'con_residuo' : 'sin_residuo');
    }
    if (operation === 'mult') {
      return 'mult.' + Math.max(aDigits, bDigits) + 'd_x_' + Math.min(aDigits, bDigits) + 'd';
    }
    const hasRegrouping = operation === 'suma'
      ? this.hasCarry(g.a, g.b)
      : this.hasBorrow(g.a, g.b);
    const largest = Math.max(aDigits, bDigits);
    if (largest === 1) return operation + '.1d';
    return operation + '.' + largest + 'd.' + (hasRegrouping ? 'con_reagrupacion' : 'sin_reagrupacion');
  }

  hasCarry(a, b) {
    while (a > 0 || b > 0) {
      if ((a % 10) + (b % 10) >= 10) return true;
      a = Math.floor(a / 10);
      b = Math.floor(b / 10);
    }
    return false;
  }

  hasBorrow(a, b) {
    while (a > 0 || b > 0) {
      if ((a % 10) < (b % 10)) return true;
      a = Math.floor(a / 10);
      b = Math.floor(b / 10);
    }
    return false;
  }

  generateMissionSet(missionType, count, operation = 'suma', options = {}) {
    const generator = operation === 'resta' ? genSubtraction : operation === 'mult' ? genMultiplication : operation === 'division' ? genDivision : genAddition;
    const seen = new Set();
    const problems = [];
    const focusQueue = (options.focusTags || []).slice(0, 3);
    let zeroCount = 0;
    let previousHadZero = false;
    let guard = 0;

    while (problems.length < count && guard < count * 300) {
      guard += 1;
      const g = generator(missionType);
      const key = this.canonicalMissionKey(g, operation);
      const tag = this.skillTagFor(g, operation);
      const hasZero = this.hasZeroOperand(g);
      const isTarget = !focusQueue.length || focusQueue[0] === tag;

      if (!isTarget || seen.has(key) || this.isTrivial(g, operation)) continue;
      // El cero sigue siendo parte del currículo, pero no puede monopolizar
      // la partida ni salir en dos retos consecutivos.
      if (hasZero && (zeroCount >= 2 || previousHadZero)) continue;

      seen.add(key);
      if (hasZero) zeroCount += 1;
      previousHadZero = hasZero;
      const problem = operation === 'division' ? this.buildDivisionProblem(g) : this.buildProblem(operation, g.a, g.b);
      problem.skillTag = tag;
      problems.push(problem);
      if (focusQueue.length) focusQueue.shift();
    }

    // La guarda solo se activa en bancos extremadamente pequeños. Conserva
    // ejercicios válidos antes que dejar una misión incompleta.
    while (problems.length < count) {
      const g = generator(missionType);
      const key = this.canonicalMissionKey(g, operation);
      if (seen.has(key) || this.isTrivial(g, operation)) continue;
      seen.add(key);
      const problem = operation === 'division' ? this.buildDivisionProblem(g) : this.buildProblem(operation, g.a, g.b);
      problem.skillTag = this.skillTagFor(g, operation);
      problems.push(problem);
    }
    return problems;
  }
}
export const mathEngine=new MathEngine();
